import type { QueueStep, QueueViewId, StockAdminQueue } from '@myrewards/contracts';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { pageOffset } from '@myrewards/contracts';
import { useMemo, useRef, useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { useSearchParams } from 'react-router';
import { meQuery, waitingQuery } from '../../api';
import { anyAwardsQuery } from '../../api/homes';
import { pastDeadlineQuery } from '../../api/letters';
import { queueQuery } from '../../api/ops';
import { Hero, PageBody, TwoColumns, type HeroAction } from '../../shell';
import {
  Card,
  CardHelp,
  clientPage,
  cx,
  DataTable,
  EmptyState,
  FilterBar,
  formatCount,
  formatDate,
  formatMoney,
  formatWeekdayDate,
  Pager,
  plural,
  StatusPill,
  useClampedPage,
  usePaging,
  type DataColumn,
} from '../../ui';
import {
  CHECK_NEED,
  QUEUE_VIEW,
  STEP,
  STEP_NOTE,
  TYPE_SHORT,
  WORK_HREF,
  optionsWords,
} from '../labels';
import { oldestLetter } from '../elections/presenter';
import { BackLink, Notice, PersonChip } from '../shared';
import {
  doneForOthersNote,
  HELD_FOR_WORDS,
  queueMark,
  stillCountsNote,
} from '../../acting-for/doneWords';
import { CheckDetails } from './CheckDetails';
import { ClaimButton } from './ClaimButton';
import {
  MarkCheckedBar,
  NO_SELECTION,
  readyRows,
  SelectAward,
  type QueueSelection,
} from './MarkChecked';
import { QueueViews } from './QueueViews';
import { ValuesInUseCard } from '../../home/ValuesInUseCard';
import {
  age,
  claimApplies,
  emptyView,
  queueHeroStats,
  queueSentence,
  rowHref,
  stepFromParam,
  stepHref,
  stepLabel,
  STEPS,
  stepTone,
  waitingOnWords,
  automaticLettersWords,
  lettersWords,
  visibleRows,
} from './presenter';
import { LettersFacts } from './LettersFacts';
import { NoMatches } from './NoMatches';
import {
  QUEUE_MY_WORK_FIRST,
  QUEUE_MY_WORK_WORDS,
  queueSorts,
  type QueueFilterId,
} from './sorting';
import { useQueueTable } from './useQueueTable';
import { TodayCard } from './TodayCard';
import { WhoIsWorkingCard } from './WhoIsWorkingCard';
import s from '../Ops.module.css';
import { ManageTabs } from '../../manage/ManageTabs';

type Row = StockAdminQueue['rows'][number];

/** What each row's box shows and does; a row only counts while it's still ready (APP-105). */
type Selection = { has: (id: string) => boolean; toggle: (id: string, on: boolean) => void };

/** Under an award at the check: ready to mark checked from here, or what it still needs. */
function checkWords(row: Row): string | null {
  if (row.check === null) return null;
  if (row.check.ready) return 'Ready to mark checked';
  const needs = row.check.needs.flatMap((n) => (n === 'claimed' ? [] : [CHECK_NEED[n]]));
  return needs.length === 0 ? null : needs.join(' · ');
}

function columns(
  asOf: string,
  meId: string,
  selection: Selection | null,
  processes: boolean,
): DataColumn<Row>[] {
  // Every column sorts (APP-149 D3), the same way as on the Home card.
  const sort = queueSorts(meId, asOf);
  return [
    {
      id: 'award',
      header: 'Award',
      cell: (row) => {
        const award = (
          <span>
            <Link href={rowHref(row)} className={s.strongLink}>
              {row.award.displayId}
            </Link>
            <span className={s.sub}>{row.subject}</span>
          </span>
        );
        if (selection === null) return award;
        return (
          <span className={s.selectAward}>
            {row.check?.ready === true ? (
              <SelectAward
                row={row}
                selected={selection.has(row.award.id)}
                onChange={(on) => {
                  selection.toggle(row.award.id, on);
                }}
              />
            ) : (
              <span className={s.selectSpacer} aria-hidden="true" />
            )}
            {award}
          </span>
        );
      },
      nowrap: true,
      card: 'title',
      sort: sort.award,
    },
    {
      id: 'stage',
      header: 'Stage',
      cell: (row) => (
        <>
          <StatusPill size="sm" tone={stepTone(row)} label={stepLabel(row)} />
          {waitingOnWords(row) !== null && <span className={s.sub}>{waitingOnWords(row)}</span>}
          {lettersWords(row) !== null && <span className={s.sub}>{lettersWords(row)}</span>}
          {/* APP-244: MyRewards making the letters, or why it couldn't. */}
          {automaticLettersWords(row) !== null && (
            <span className={cx(s.sub, automaticLettersWords(row)?.problem === true && s.negative)}>
              {automaticLettersWords(row)?.words}
            </span>
          )}
          {checkWords(row) !== null && (
            <span className={cx(s.sub, row.check?.ready === true && s.ready)}>
              {checkWords(row)}
            </span>
          )}
          {/* APP-130 D18: worked out live, so it clears when the letter arrives. */}
          {(row.letterGap ?? 0) > 0 && (
            <span className={cx(s.sub, s.negative)}>
              {plural(row.letterGap ?? 0, 'letter', 'letters')} can’t be made yet
            </span>
          )}
          {/* APP-148 (the Check board): what was done for someone on it, oldest first. */}
          {(row.doneForOthers ?? []).map((mark, i) => (
            <span key={`${mark.actor.id}-${mark.action}-${String(i)}`} className={s.sub}>
              {queueMark(mark, meId)}
            </span>
          ))}
        </>
      ),
      nowrap: true,
      sort: sort.stage,
    },
    {
      id: 'claim',
      header: 'Claim',
      card: 'action',
      cell: (row) => {
        // D17: held by "Not what you asked?", nobody claims it until it's settled. Done for
        // someone without a question, it keeps Claim like any other (D11: no identity rules).
        const held =
          row.held === true ? (
            <span className={cx(s.sub, s.negative)}>
              {row.heldFor == null ? 'Held until it’s settled' : HELD_FOR_WORDS[row.heldFor]}
            </span>
          ) : null;
        if (row.claimedBy !== null) {
          const you = row.claimedBy.id === meId;
          return (
            <>
              <span className={s.sub}>Claimed by</span>
              <PersonChip
                initials={row.claimedBy.initials}
                name={row.claimedBy.name}
                tone={you ? 'cyan' : 'sky'}
              />
              {held}
            </>
          );
        }
        if (held !== null) return held;
        // A System Admin sees the queue without working it (APP-134): no Claim to press.
        if (claimApplies(row) && !processes)
          return <span className={s.muted}>Waiting for a claim</span>;
        if (claimApplies(row))
          return (
            <ClaimButton
              awardId={row.award.id}
              displayId={row.award.displayId}
              version={row.version}
              to={rowHref(row)}
            />
          );
        return <span className={s.muted}>No claim at this step</span>;
      },
      nowrap: true,
      sort: sort.claim,
    },
    // When it was submitted, and by whom, follows Stage and Claim so the date shows without
    // scrolling the table sideways (APP-127). Supporting details come after it.
    {
      id: 'submitted',
      header: 'Submitted',
      cell: (row) => (
        <>
          <span className={s.nowrap}>{formatDate(row.submittedAt)}</span>
          <span className={s.sub}>Sent by {row.submittedBy.name}</span>
        </>
      ),
      sort: sort.submitted,
    },
    { id: 'type', header: 'Type', cell: (row) => TYPE_SHORT[row.type], sort: sort.type },
    { id: 'pool', header: 'Sector / pool', cell: (row) => row.pool, sort: sort.pool },
    {
      id: 'people',
      header: 'People',
      cell: (row) => formatCount(row.people),
      numeric: true,
      sort: sort.people,
    },
    {
      id: 'cash',
      header: 'Cash',
      cell: (row) => formatMoney(row.cashCents),
      numeric: true,
      sort: sort.cash,
    },
    {
      id: 'options',
      header: 'Options',
      cell: (row) => optionsWords(row.options),
      numeric: true,
      sort: sort.options,
    },
    // How long it has been with the team.
    {
      id: 'age',
      header: 'Age',
      cell: (row) => age(row.since, asOf),
      nowrap: true,
      sort: sort.age,
    },
  ];
}

/**
 * The filters that mean something in each view (APP-149 D4, D5): My queue is all yours, so no
 * claim; Letters is one stage, so no stage. A step chosen on Everything in progress is its stage,
 * so it drops the Stage filter too.
 */
const VIEW_FILTERS: Partial<Record<QueueViewId, readonly QueueFilterId[]>> = {
  queue: ['claim', 'stage', 'type', 'pool', 'submitted'],
  mine: ['stage', 'type', 'pool', 'submitted'],
  in_progress: ['claim', 'stage', 'type', 'pool', 'submitted'],
  letters: ['claim', 'type', 'pool', 'submitted'],
};
const STEP_FILTERS: readonly QueueFilterId[] = ['claim', 'type', 'pool', 'submitted'];

function StepTiles({
  queue,
  active,
}: {
  queue: StockAdminQueue;
  active: QueueStep | null;
}): ReactElement {
  const count = (id: QueueStep): number => queue.steps.find((x) => x.id === id)?.count ?? 0;
  return (
    <section className={s.steps} aria-labelledby="queue-steps">
      <div className={s.stepsHead}>
        <h2 id="queue-steps" className={cx('eyebrow', s.stepsTitle)}>
          All sectors · awards by stage
        </h2>
        <CardHelp
          topic="Awards by stage"
          shows="Every award with the Stock Admin team or at a later step, counted by its current stage."
          canDo="Choose a step to list just those awards under Everything in progress."
        />
      </div>
      <ul className={s.stepList}>
        {STEPS.map((id) => {
          const n = count(id);
          return (
            <li key={id}>
              <Link
                href={stepHref(id)}
                className={cx(s.stepTile, id === 'past_deadline' && n > 0 && s.stepProblem)}
                {...(active === id && { 'aria-current': 'true' as const })}
              >
                <span className={cx('eyebrow', s.stepLabel)}>{STEP[id].label}</span>
                <span className={cx('disp', s.stepCount)}>{formatCount(n)}</span>
                <span className={s.stepNote}>{n === 0 ? STEP[id].none : STEP_NOTE[id]}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/**
 * A view with nothing in it: caught up once work has been and gone, else what will land here. A
 * step filter that matches nothing offers everything in progress. "How the queue works" (SA1's
 * board) would open Help at the queue's topic, but Help opens only from the shell, not a link, so
 * the caught-up queue offers everything in progress instead; the card's own help explains it.
 */
function EmptyQueue({
  view,
  step,
  empty,
}: {
  view: QueueViewId;
  step: QueueStep | null;
  empty: ReturnType<typeof emptyView>;
}): ReactElement {
  if (empty.caughtUp)
    return (
      <EmptyState
        variant="caught-up"
        headline={empty.headline}
        body={empty.body}
        {...(view === 'queue' && {
          action: { label: 'See everything in progress', href: QUEUE_VIEW.in_progress.href },
        })}
      />
    );
  return (
    <EmptyState
      variant="first-day"
      art={step !== null ? 'search' : view === 'letters' ? 'letter' : 'calendar'}
      headline={empty.headline}
      body={empty.body}
      {...(step !== null
        ? { action: { label: 'Show everything in progress', href: QUEUE_VIEW.in_progress.href } }
        : view !== 'queue' && { action: { label: 'Open the queue', href: QUEUE_VIEW.queue.href } })}
    />
  );
}

/** SA1: the Stock Admin team's queue, its views and step counts, claims and who's on what. */
export function QueuePage({ view }: { view: QueueViewId }): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const { data: waiting } = useSuspenseQuery(waitingQuery);
  const { data: queue } = useSuspenseQuery(queueQuery(view));
  const [params] = useSearchParams();
  const step = view === 'in_progress' ? stepFromParam(params.get('step')) : null;
  const inView = useMemo(() => visibleRows(queue, step), [queue, step]);
  const asOf = waiting.asOf;
  const meId = me.person.id;
  const count = (id: QueueStep): number => queue.steps.find((x) => x.id === id)?.count ?? 0;
  // Day one or caught up (QA-030): the same read Home uses; unknown while it loads or if refused.
  const everReached = useQuery(anyAwardsQuery);
  const history = everReached.data ?? null;
  const empty = emptyView(view, step, queue.teammates, history);
  const barRef = useRef<HTMLDivElement>(null);
  // APP-149: sorted and filtered over the whole view, then paged (D3, D4); one saved sort for every
  // view (B6). Sorting reads only the columns' names and sorts, the same with or without ticks.
  const sortColumns = useMemo(
    () => columns(asOf, meId, null, queue.processes),
    [asOf, meId, queue.processes],
  );
  const table = useQueueTable({
    table: 'queue',
    rows: inView,
    columns: sortColumns,
    filterIds: step === null ? (VIEW_FILTERS[view] ?? STEP_FILTERS) : STEP_FILTERS,
    meId,
    asOf,
    barRef,
    // APP-244: my work first, then what waits for a claim; every filter stays.
    defaultSort: QUEUE_MY_WORK_FIRST,
    defaultWords: QUEUE_MY_WORK_WORDS,
  });
  const rows = table.rows;
  // APP-135: the queue is paged on the screen (the server sends the whole view).
  const paging = usePaging('queue');
  const { anchorRef } = paging;
  const paged = clientPage(rows, paging.page, paging.size);
  useClampedPage(paging, paged.page.number);
  const pageRows = paged.rows;
  // APP-105: the awards ticked to mark checked together, on the lists that hold checks. Ticks act
  // on the page shown; "Select all N ready, on every page" (APP-135 §5) reaches the rest.
  const [selected, setSelected] = useState<QueueSelection>(NO_SELECTION);
  // A new view, step or filter clears everything; a new page, size or sort clears page ticks, not
  // the every-page choice. Done while rendering, so no stale tick ever shows.
  const viewKey = `${view}:${step ?? ''}:${JSON.stringify(table.bar.filters.map((f) => f.value))}`;
  const pageKey = `${viewKey}:${table.key}:${String(paged.page.number)}:${String(paging.size)}`;
  const [seen, setSeen] = useState({ viewKey, pageKey });
  if (seen.pageKey !== pageKey) {
    setSeen({ viewKey, pageKey });
    if (seen.viewKey !== viewKey || selected.mode === 'page') setSelected(NO_SELECTION);
  }
  const atCheck = rows.filter((r) => r.check !== null);
  const pageAtCheck = pageRows.filter((r) => r.check !== null);
  // Ticking to mark checked is the team's; a System Admin only reads the details (APP-134).
  const selection: Selection | null =
    atCheck.length > 0 && queue.processes
      ? {
          // After the every-page choice, an award it named that is no longer ready isn't ticked.
          has: (id) =>
            selected.ids.has(id) &&
            (selected.mode === 'page' || readyRows(rows).some((r) => r.award.id === id)),
          toggle: (id, on) => {
            // Unticking one while every page is selected falls back to the page you see.
            const base =
              selected.mode === 'all'
                ? new Set(
                    readyRows(pageRows)
                      .filter((r) => selected.ids.has(r.award.id))
                      .map((r) => r.award.id),
                  )
                : new Set(selected.ids);
            if (on) base.add(id);
            else base.delete(id);
            setSelected({ mode: 'page', ids: base });
          },
        }
      : null;
  // APP-109: each award at the check opens its details in place; the bar opens the page's.
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const allOpen = pageAtCheck.length > 0 && pageAtCheck.every((r) => open.has(r.award.id));
  // SA1's board says how long the oldest has waited ("13 days since the letter"): SA4's own read,
  // only when something is past its deadline. Until it answers, the number says what to do.
  const late = useQuery({
    ...pastDeadlineQuery,
    enabled: count('past_deadline') > 0,
    retry: false,
  });
  const oldest = late.data === undefined ? null : oldestLetter(late.data.items);

  // APP-148 (the Check board): what the reader did for someone among these, and that it counts.
  const doneNote = doneForOthersNote(
    inView,
    meId,
    step === null ? queue.doneForOthersByYou : undefined,
  );
  const waitingCount = count('waiting_for_claim');

  const shareworks: HeroAction = { label: 'Shareworks worklist', href: WORK_HREF.shareworks };
  const primary: HeroAction | null =
    view === 'mine'
      ? { label: 'Open the queue', href: QUEUE_VIEW.queue.href, icon: 'arrowLeft' }
      : queue.processes
        ? { label: 'Open my queue', href: QUEUE_VIEW.mine.href, icon: 'check' }
        : null;

  return (
    <>
      <Hero
        eyebrow={`Stock administration · ${formatWeekdayDate(asOf)}`}
        title={view === 'queue' ? 'Work queue' : QUEUE_VIEW[view].label}
        sub={queueSentence(queue, meId, history)}
        actions={primary === null ? [shareworks] : [primary, shareworks]}
        stats={queueHeroStats(queue, view, history, oldest?.days ?? null)}
        titleTestId="queue-title"
      />
      <ManageTabs current={view === 'letters' ? 'letters' : 'queue'} />
      <PageBody nav={<QueueViews queue={queue} queueHref="/manage/queue" />}>
        <div className={s.stack}>
          {view !== 'queue' && <BackLink href={QUEUE_VIEW.queue.href}>Queue</BackLink>}
          <StepTiles queue={queue} active={step} />
          {doneNote !== null && <Notice tone="info">{doneNote}</Notice>}
          <Card
            title={QUEUE_VIEW[view].label}
            helpAnchor="work-queue"
            help={{
              shows:
                'All active submitted awards, from approval through Shareworks, the oldest submitted first, with their stage and the name of anyone holding a claim.',
              canDo:
                'Sort by any heading, or filter by claim, stage, type, sector or pool and when it was submitted. Open an award’s details to see its people, amounts, charge codes and proof here, tick the ones that are ready and mark them checked together, or claim one to check it on its own page; your claim shows to the whole team. Open My queue or a stage to narrow the list.',
            }}
            aside={
              step !== null ? (
                <span className={s.filterNote}>
                  Showing {STEP[step].label.toLowerCase()} only ·{' '}
                  <Link href={QUEUE_VIEW.in_progress.href} className={s.inlineLink}>
                    Show everything in progress
                  </Link>
                </span>
              ) : undefined
            }
          >
            {/* Nothing to sort or filter in an empty view: its own message stands alone. */}
            {inView.length > 0 && (
              <div ref={barRef}>
                <FilterBar label={`Filter ${QUEUE_VIEW[view].label}`} {...table.bar} />
              </div>
            )}
            {rows.length > 0 && selection !== null && (
              <MarkCheckedBar
                rows={rows}
                pageRows={pageRows}
                selection={selected}
                onSelect={setSelected}
                details={{
                  allOpen,
                  onAllOpen: (on) => {
                    const next = new Set(open);
                    for (const r of pageAtCheck) {
                      if (on) next.add(r.award.id);
                      else next.delete(r.award.id);
                    }
                    setOpen(next);
                  },
                }}
              />
            )}
            <section ref={anchorRef} tabIndex={-1}>
              {/* Empty, the header row stays and the day-one or caught-up message is its one row
                (DESIGN-SPEC §5, APP-093); on a phone the message stands alone. A filter that
                leaves nothing says so, with the way back (APP-149). */}
              <DataTable
                caption={
                  step === null
                    ? QUEUE_VIEW[view].label
                    : `${QUEUE_VIEW[view].label} · ${STEP[step].label}`
                }
                columns={columns(asOf, meId, selection, queue.processes)}
                rows={pageRows}
                rowKey={(row) => row.award.id}
                minWidth={920}
                cards
                empty={{ title: empty.headline, body: empty.body }}
                emptyState={
                  inView.length > 0 ? (
                    <NoMatches onClear={table.clearFromEmpty} />
                  ) : (
                    <EmptyQueue view={view} step={step} empty={empty} />
                  )
                }
                {...(inView.length > 0 && { sort: table.sort, onSortChange: table.setSort })}
                {...(atCheck.length > 0 && {
                  details: {
                    has: (row: Row) => row.check !== null,
                    of: (row: Row) => <CheckDetails row={row} />,
                    name: (row: Row) => row.award.displayId,
                    open,
                    onOpenChange: setOpen,
                  },
                })}
              />
            </section>
            <Pager
              label={QUEUE_VIEW[view].label}
              page={paged.page}
              onPage={(n) => {
                paging.setPage(n);
              }}
              onSize={(size) => {
                paging.setSize(size, pageOffset(paged.page));
              }}
            />
            {doneNote !== null && view === 'queue' && waitingCount > 0 && (
              <p className={s.muted}>{stillCountsNote(waitingCount, queue.teammates)}</p>
            )}
          </Card>
          {/* SA3's day-one facts: the templates, who gets each letter, where facts come from. */}
          {view === 'letters' && <LettersFacts />}
          <TwoColumns
            sideLabel="The Stock Admin team"
            main={<TodayCard teammates={queue.teammates} />}
            side={
              <div className={s.stack}>
                <WhoIsWorkingCard
                  teammates={queue.teammates}
                  showInProgressLink={view !== 'in_progress'}
                />
                <ValuesInUseCard />
              </div>
            }
          />
        </div>
      </PageBody>
    </>
  );
}
